import React, { FC, useState } from 'react';
import {
  Box,
  Text,
  Button,
  ButtonAppearance,
  ToastAction,
  ToastType,
  useToastsDispatch,
  SpinnerSize,
  Spinner,
} from '@bechtel/gds';
import { AddReportContainerStyled } from './styled';
import { Trans, t } from '@lingui/macro';
import { Formik, ErrorMessage } from 'formik';
import { object, string } from 'yup';
import { AddNewReportProps, AddDataFormikState } from './types';
import { AddProjectReportInput, AddProjectReportPayload, AddProjectReportError } from '@bechtel/types';
import { ADD_PROJECT_REPORT } from '@bechtel/client-gql';
import { useMutation } from '@apollo/client';
import { get } from 'lodash';
import { getError } from '../error';
import { ModalError, ErrorModal, FormField, ErrorBoundary } from '@bechtel/client-common';
import { useLingui } from '@lingui/react';
import { clearCacheByKey, QueryKeys } from '~/services/cache';

const initialValues: AddDataFormikState = {
  name: '',
  description: '',
  powerbiUrl: '',
  isActive: true,
};

const urlRegex = /^https:\/\/app\.powerbi\.com/;
const addReportValidationSchema = object().shape({
  name: string()
    .trim()
    .min(5, t`Please enter atleast one character for name!`)
    .max(100, t`Too Long!`)
    .required(t`Name is mandatory`),
  description: string()
    .trim()
    .min(5, t`Please enter atleast one character for description!`)
    .max(2000, t`Too Long!`),
  powerbiUrl: string()
    .matches(urlRegex, t`Enter correct url!`)
    .required(t`Power BI URL is mandatory`),
});

const ReportUpdateToast = () => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        {t`Report added successfully.`}
      </Text>
    </Box>
  );
};
const createSuccessToast: ToastAction = {
  type: 'ADD_TOAST',
  payload: {
    toastType: ToastType.SUCCESS,
    content: ReportUpdateToast,
    autoDismissTimeout: 5000,
  },
};

export const FieldError = ({ msg }) => {
  const { i18n } = useLingui();
  return (
    <Box pt={{ sm: '400' }} pb={{ sm: '500' }}>
      <Text color="bechtel.red">{i18n._(msg)}</Text>
    </Box>
  );
};
export const AddNewReport: FC<AddNewReportProps> = ({ close, setLoadReportList }) => {
  const { i18n } = useLingui();
  const [addReportError, setAddReportError] = useState<ModalError<AddProjectReportError>>(null);
  const dispatchToast = useToastsDispatch();
  const [addReportData, { loading }] = useMutation<
    { addProjectReport: AddProjectReportPayload },
    { input: AddProjectReportInput }
  >(ADD_PROJECT_REPORT, {
    onCompleted: (data) => {
      const addReportError = get(data, 'addProjectReport.error');
      if (addReportError) {
        setAddReportError(addReportError);
      } else {
        setAddReportError(null);
        dispatchToast(createSuccessToast);
        setLoadReportList(true);
        close();
      }
    },
    onError: (error) => {
      setAddReportError(error);
    },
    update: (cache, result) => {
      if (!result.data?.addProjectReport.error) {
        clearCacheByKey(cache, QueryKeys.REPORTS);
      }
    },
  });

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={addReportValidationSchema}
        onSubmit={(values) => {
          addReportData({
            variables: {
              input: {
                name: values.name.trim(),
                description: values.description.trim(),
                powerbiUrl: encodeURIComponent(values.powerbiUrl.replace(/'/g, '%27')),
                isActive: values.isActive,
              },
            },
          });
          setLoadReportList(false);
        }}
      >
        {({ dirty, isValid, handleSubmit }) => {
          return (
            <AddReportContainerStyled>
              <Box mb={{ sm: '550' }} ml={{ sm: '400' }}>
                <Text family="graphik" size="500" weight="600">
                  <Trans>Add Report</Trans>
                </Text>
              </Box>
              <Box px={{ sm: '400' }} pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                <FormField labelName={t`Name`} fieldName="name" placeholder={t`Enter name`} />
                <ErrorMessage name="name">
                  {(msg) => {
                    return <FieldError msg={i18n._(msg)} />;
                  }}
                </ErrorMessage>
              </Box>
              <Box px={{ sm: '400' }} pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                <FormField labelName={t`Description`} fieldName="description" placeholder={t`Enter Description`} />
                <ErrorMessage name="description">
                  {(msg) => {
                    return <FieldError msg={i18n._(msg)} />;
                  }}
                </ErrorMessage>
              </Box>
              <Box px={{ sm: '400' }} pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                <FormField labelName={t`Power BI URL`} fieldName="powerbiUrl" placeholder={t`Enter Power BI URL`} />
                <ErrorMessage name="powerbiUrl">
                  {(msg) => {
                    return <FieldError msg={i18n._(msg)} />;
                  }}
                </ErrorMessage>
              </Box>
              <Box px={{ sm: '400' }} justifyContent={{ sm: 'space-between' }}>
                <Button appearance={ButtonAppearance.TERTIARY} disabled={loading} onClick={close}>
                  <Text family="graphikCompact" weight="600" size="400" color="bechtel.blue.800">
                    <Trans>Cancel</Trans>
                  </Text>
                </Button>

                <Button
                  appearance={ButtonAppearance.TERTIARY}
                  disabled={!dirty || !isValid}
                  onClick={() => handleSubmit()}
                >
                  {loading ? (
                    <Spinner size={SpinnerSize.SMALL} />
                  ) : (
                    <Text family="graphikCompact" weight="600" size="400" color="bechtel.blue.800">
                      <Trans>Save</Trans>
                    </Text>
                  )}
                </Button>
              </Box>
            </AddReportContainerStyled>
          );
        }}
      </Formik>
      {addReportError && (
        <ErrorBoundary>
          <ErrorModal
            title={t`Add Report Error`}
            error={getError(addReportError)}
            close={() => setAddReportError(null)}
          />
        </ErrorBoundary>
      )}
    </>
  );
};
